import { useEffect, useState } from "react";
import { X, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { priceDisplay, localCurrency } from "../lib/format";
import { useGeo } from "../lib/useGeo";

const SHOWN_KEY = "bb_exit_intent_shown_v1";

/**
 * Exit-intent popup with a recovery discount. Detects when the visitor
 * moves the cursor outside the top of the viewport (desktop) OR when the
 * tab is hidden / back-navigated (mobile fallback). Shows ONCE per session
 * — never re-pops after dismissal in the same browser.
 *
 * onClaim() is called when the user takes the offer — typically wired to
 * the same startCheckout() handler the hero CTA uses.
 */
export default function ExitIntentPopup({ onClaim, priceOverride }) {
  const [open, setOpen] = useState(false);
  const country = useGeo();
  const mainDefault = priceDisplay(27, country);
  const offerDefault = priceDisplay(22, country);
  const localUnitDefault = localCurrency(country);
  // priceOverride = { main: {primary, primaryNumber, currencyShort, secondary}, offer: {...}, localUnit: "جنيه" }
  const main = priceOverride?.main || mainDefault;
  const offer = priceOverride?.offer || offerDefault;
  const localUnit = priceOverride?.localUnit || localUnitDefault;
  const discount = main.primaryNumber - offer.primaryNumber;

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      if (window.sessionStorage.getItem(SHOWN_KEY)) return; // already shown this session
    } catch (_) { /* ignore */ }

    let armed = false;
    // Arm only after 8s on the page so we don't ambush bouncing visitors.
    const armTimer = setTimeout(() => { armed = true; }, 8000);

    const trigger = () => {
      if (!armed || open) return;
      try { window.sessionStorage.setItem(SHOWN_KEY, "1"); } catch (_) { /* ignore */ }
      setOpen(true);
    };

    const onMouseLeave = (e) => {
      // Desktop exit-intent: cursor moves into the top OS chrome
      if (e.clientY <= 0) trigger();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") trigger();
    };

    document.addEventListener("mouseleave", onMouseLeave);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseleave", onMouseLeave);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [open]);

  const close = () => setOpen(false);
  const claim = () => {
    close();
    if (typeof onClaim === "function") onClaim();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6"
      data-testid="exit-intent-overlay"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-gradient-to-br from-[#0B1D3A] to-[#040914] border border-[#C5A059]/60 rounded-sm p-6 sm:p-8 shadow-[0_0_80px_rgba(197,160,89,0.35)] animate-in fade-in zoom-in duration-300"
        data-testid="exit-intent-card"
      >
        <button
          onClick={close}
          aria-label="إغلاق"
          data-testid="exit-intent-close"
          className="absolute top-3 left-3 text-[#A1B0C6] hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 text-[#C5A059] mb-3">
          <Sparkles className="w-4 h-4" />
          <span className="text-xs tracking-[0.3em] font-bold">عرض خاص</span>
        </div>

        <h3 className="font-display text-2xl sm:text-3xl font-black text-white leading-tight mb-3">
          انتظر… قبل ما تغادر
        </h3>
        <p className="text-sm sm:text-base text-[#A1B0C6] leading-relaxed mb-5">
          نعرف أن قرار الشراء مهم. <span className="text-[#C5A059] font-bold">خصم {discount} {localUnit} لك الآن</span> لو أكملت الطلب خلال الدقائق القادمة.
        </p>

        <div className="bg-[#040914]/60 border border-[#C5A059]/25 rounded-sm p-4 mb-5">
          <div className="flex items-baseline justify-center gap-3">
            <span className="text-base text-white/40 line-through">{main.primary}</span>
            <span className="font-display text-5xl font-black text-[#C5A059]">{offer.primaryNumber} <span className="text-2xl">{offer.currencyShort}</span></span>
          </div>
          <div className="text-center text-xs text-[#C5A059]/85 mt-1 font-semibold">
            {offer.secondary ? `${offer.secondary} USD · ` : ""}تسليم فوري
          </div>
        </div>

        <Button
          onClick={claim}
          data-testid="exit-intent-claim-btn"
          className="w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] font-black text-lg py-6 rounded-sm shadow-[0_0_30px_rgba(197,160,89,0.4)]"
        >
          أكمل الطلب بسعر {offer.primary}
        </Button>

        <button
          onClick={close}
          className="block w-full text-center text-xs text-[#A1B0C6]/70 hover:text-[#A1B0C6] mt-3"
          data-testid="exit-intent-decline"
        >
          لا، شكراً
        </button>
      </div>
    </div>
  );
}
